import { bearingDeg, bearingDiff } from './geo';
import type { CleanFix } from './types';

export interface Intro {
  /** Spoken lead-in: the landmark's name, e.g. "Multnomah Falls." */
  text: string;
  /** Rough spoken length, to add to the script's own estimate. */
  seconds: number;
  side: 'left' | 'right' | 'ahead' | 'behind' | null;
  /** Seconds until closest approach along the heading, when moving. */
  etaSec: number | null;
}

const WORDS_PER_SECOND = 2.6;
const MOVING_MPS = 3;
/** Within this of dead ahead the landmark counts as straight ahead rather than on a side. */
const AHEAD_CONE_DEG = 20;
/** Beyond this the landmark is already behind the car (only reachable inside the base radius). */
const BEHIND_DEG = 110;

/** "Vista House (Oregon)" -> "Vista House". Wikipedia disambiguators are not for the ear. */
export function spokenTitle(title: string): string {
  return title.replace(/\s*\([^)]*\)\s*$/, '').trim() || title;
}

/**
 * Signed angle from the car's heading to the landmark, in (-180, 180]: negative is to the left.
 * Null without a heading. This is a straight line, so on a winding road it swings as the road
 * bends; the on-screen arrow redraws it every fix, which is why the arrow can show it and speech can't.
 */
export function relativeBearing(lm: { lat: number; lon: number }, fix: CleanFix): number | null {
  if (fix.heading == null) return null;
  return bearingDiff(fix.heading, bearingDeg(fix.coords.latitude, fix.coords.longitude, lm.lat, lm.lon));
}

/**
 * The opening the driver hears before a script, plus where the landmark sits relative to the car.
 *
 * Only the name is spoken. A spoken side and timing ("Coming up on your left in about a minute")
 * were dropped after the first real drive (2026-09-19): a straight-line bearing from a winding road
 * named the wrong side often enough to mislead. Side and eta are still computed, for the log and
 * for a future on-screen arrow that can update as the road bends.
 */
export function narrationIntro(lm: { title: string; lat: number; lon: number }, fix: CleanFix, distM: number): Intro {
  const moving = fix.speedMps != null && fix.speedMps >= MOVING_MPS;

  let side: Intro['side'] = null;
  const rel = relativeBearing(lm, fix);
  if (rel != null) {
    const a = Math.abs(rel);
    side = a <= AHEAD_CONE_DEG ? 'ahead' : a > BEHIND_DEG ? 'behind' : rel < 0 ? 'left' : 'right';
  }

  let etaSec: number | null = null;
  if (moving) {
    // Distance along the direction of travel, so something abeam does not read as a minute out.
    const along = rel != null ? Math.max(0, distM * Math.cos((rel * Math.PI) / 180)) : distM;
    etaSec = along / fix.speedMps!;
  }

  const text = `${spokenTitle(lm.title)}.`;
  return { text, seconds: Math.ceil(text.split(/\s+/).length / WORDS_PER_SECOND), side, etaSec };
}

/** One-line diagnostic for logs: "left, ~60 s" or "ahead, 1.2 km". */
export function describeIntro(intro: Intro, distM: number): string {
  const where = intro.side ?? 'no heading';
  const when = intro.etaSec != null ? `~${Math.round(intro.etaSec)} s` : `${(distM / 1000).toFixed(1)} km`;
  return `${where}, ${when}`;
}
